import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

import { formatInTimezone } from '@/utils/date'
import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'
import PrimaryButton from '@/shared/Buttons/Primary'
import DefaultButton from '@/shared/Buttons/Default'

const EventRow = ({event, user, attendingEventIds, compact}) => {
  const { putpostRequest } = useQuery()
  const [ currentUser ] = useGlobalState('currentUser')
  const [, setToast] = useGlobalState('toast')

  const [ attending, setAttending ] = useState(false)
  const [ loading, setLoading ] = useState(false)
  const ticketTypes = event.ticketTypes || {}
  const attendNow = () => { window.location = `/events/links/${event.uuid}` }

  useEffect(() => {
    if (event === null) { return }

    if (Object.keys(ticketTypes).length > 0) {
      setAttending(attendingEventIds[event.id])
    } else {
      setAttending(Object.keys(attendingEventIds).includes(event.id.toString()))
    }
  }, [attendingEventIds])

  const toggleAttend = (ticketTypeId) => {
    setLoading(true)
    let data = {
      ticket_type_id: ticketTypeId
    }
    putpostRequest(`/api/v1/events/${event.id}/attend`, 'POST', data, (err, jsonData) => {
      setLoading(false)
      if (err) { console.log('toast errors'); return }

      if (ticketTypeId) {
        setAttending(attending === ticketTypeId ? null : ticketTypeId)
      } else {
        setAttending(!attending)
      }
      if (!attending === true) {
        setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
          <p className="text-sm font-medium dark:text-green-100 text-green-600">Attending!</p>
          <p className="mt-1 text-sm dark:text-green-300 text-green-600">You're going! You'll get an email about this.</p>
        </div>)
      } else { 
        setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
          <p className="text-sm font-medium dark:text-yellow-100 text-yellow-600">RSVP is canceled</p>
          <p className="mt-1 text-sm dark:text-gray-200 text-gray-900">You are not attending this event anymore.</p>
        </div>)
      }
    })
  }

  return <div className="group flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-cccblue dark:hover:border-cccblue-alt">
    { !compact && <a href={`/events/${event.id}`} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 relative overflow-hidden">
      <div className="aspect-w-16 aspect-h-9 relative">
        <img className="object-cover group-hover:scale-105 transition-transform duration-300" src={`${event.imageUrl}?aspect=16:9&width=420`} alt={event.name} />
      </div>
      {event.status === 'started' && (
        <div className="absolute top-2 right-2 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold animate-pulse z-10">
          Live Now
        </div>
      )}
      <div className="absolute top-2 left-2 bg-cccblue text-white px-3 py-1 rounded-full text-sm font-semibold z-10">
        Event
      </div>
    </a> }
    <a href={`/events/${event.id}`} target="_blank" rel="noopener noreferrer" className="flex-1 p-6 flex flex-col justify-between hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
      <div className="flex-1">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 group-hover:text-cccblue dark:group-hover:text-cccblue-alt transition-colors">{event.name}</h3>
          { !currentUser.canAttendMembersOnlyEvents && event.admittance === 'members-only' && event.courseId === null && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
              Members Only
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cccpurple bg-opacity-10 text-cccpurple dark:bg-cccpurple-alt dark:bg-opacity-20 dark:text-purple-300">
            {event.kind}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">• {event.lengthOfEvent} min</span>
        </div>
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-300 mb-3">
          <svg className="w-4 h-4 mr-1.5 text-cccblue dark:text-cccblue-alt" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          { event.status === 'started' ? (
            <span className="text-green-600 dark:text-green-400 font-medium">Event has started!</span>
          ) : (
            <time dateTime={event.startsAtUtc}>
              {formatInTimezone(event.startsAtUtc, user.timezone, 'EEE MMM d, yyyy h:mmaaa')}
            </time>
          )}
        </div>
        <div className="block">
          <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-3" dangerouslySetInnerHTML={{__html: event.description}}></p>
        </div>
      </div>
    </a>
    <div className="bg-gradient-to-r from-cccblue to-cccblue-alt p-4">
      <div className="flex items-center justify-between">
        <a 
          href={`/events/${event.id}`} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="inline-flex items-center px-4 py-2 bg-white text-cccblue rounded-lg font-semibold text-sm hover:bg-gray-100 transition-colors"
        >
          View Details
          <svg className="w-4 h-4 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>
    </div>
  </div>
}

export default EventRow
